import { useMemo, useState } from "react";
import { Row, Col, Card, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { snakeToText } from "@utils/helperFunctions";
import { useLatestWeekSnapshots } from "./api";
import { AccountHealthSnapshot, AccountHealthStatus } from "./types";
import { useStyles } from "./styles";

const STATUS_PRIORITY: Record<AccountHealthStatus, number> = {
  at_risk: 0,
  watch: 1,
  healthy: 2,
};

type TabKey = "all" | AccountHealthStatus;

const TAB_ORDER: TabKey[] = ["all", "healthy", "watch", "at_risk"];

const TAB_LABELS: Record<TabKey, string> = {
  all: "All",
  healthy: "Healthy",
  watch: "Watch",
  at_risk: "At Risk",
};

const formatLongDate = (iso: string): string => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const List = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { snapshots, latestWeekStart, isLoading } = useLatestWeekSnapshots();
  const [currentTab, setCurrentTab] = useState<TabKey>("all");

  const sortedSnapshots = useMemo(
    () =>
      [...snapshots].sort(
        (a, b) => STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status]
      ),
    [snapshots]
  );

  const counts = useMemo(() => {
    const c: Record<TabKey, number> = {
      all: snapshots.length,
      healthy: 0,
      watch: 0,
      at_risk: 0,
    };
    for (const s of snapshots) c[s.status] += 1;
    return c;
  }, [snapshots]);

  const visibleSnapshots = useMemo(
    () =>
      currentTab === "all"
        ? sortedSnapshots
        : sortedSnapshots.filter((s) => s.status === currentTab),
    [sortedSnapshots, currentTab]
  );

  const statusBadge = (status: AccountHealthStatus) => {
    const className =
      status === "at_risk"
        ? styles.statusAtRisk
        : status === "watch"
        ? styles.statusWatch
        : styles.statusHealthy;
    return <span className={className}>{snakeToText(status)}</span>;
  };

  const columns: ColumnDef<AccountHealthSnapshot>[] = [
    {
      header: "Account",
      cell: (props) => {
        const { accountSlug, accountName, accountId } = props.row.original;
        const label = accountName || accountId || "—";
        if (!accountSlug) return label;
        return (
          <span
            className={styles.accountLink}
            onClick={() => navigate(accountSlug)}
          >
            {label}
          </span>
        );
      },
    },
    {
      header: "Health",
      cell: (props) => statusBadge(props.row.original.status),
    },
    {
      header: "Reason",
      cell: (props) =>
        props.row.original.primaryDriver
          ? snakeToText(props.row.original.primaryDriver)
          : "—",
    },
    {
      header: "Sign In Slope",
      cell: (props) => (
        <span className={styles.numericCell}>
          {props.row.original.signinSlope.toFixed(2)}
        </span>
      ),
    },
    {
      header: "Project Slope",
      cell: (props) => (
        <span className={styles.numericCell}>
          {props.row.original.projectSlope.toFixed(2)}
        </span>
      ),
    },
    {
      header: "Project Ratio",
      cell: (props) => (
        <span className={styles.numericCell}>
          {`${Math.round(100 * props.row.original.projectBaselineRatio)}%`}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Row>
        <Col>
          <h1 className={styles.title}>Account Health</h1>
          {latestWeekStart && (
            <div className={styles.subtitle}>
              Last updated Week of {formatLongDate(latestWeekStart)}
            </div>
          )}
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon
                  className={styles.cardIcon}
                  icon={faShieldCheck}
                />
                <h2>Most Recent Snapshots</h2>
              </div>
            </div>
            <hr className={styles.line} />
            {isLoading ? (
              <ScopeStackSpinner />
            ) : sortedSnapshots.length === 0 ? (
              <div className={styles.emptyState}>
                <h3>No snapshots available</h3>
                <p>
                  Account health snapshots are generated weekly. If none are
                  showing here, the most recent run may not have completed.
                </p>
              </div>
            ) : (
              <>
                <Nav
                  className={styles.tabsNav}
                  activeKey={currentTab}
                  onSelect={(k) => setCurrentTab((k as TabKey) || "all")}
                >
                  {TAB_ORDER.map((key) => (
                    <Nav.Item key={key}>
                      <Nav.Link eventKey={key}>
                        {`${TAB_LABELS[key]} (${counts[key]})`}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
                <DataTable
                  data={visibleSnapshots}
                  columns={columns}
                  striped
                  bordered
                />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default List;
